import React, { useEffect, useRef, useState } from "react";
import { useMatch } from "react-router-dom";

import styled from "styled-components";

import AiCursor from "components/atoms/AiCursor";
import Icon from "components/atoms/Icon";
import Tooltip from "components/atoms/Tooltip";
import Alert from "components/atoms/alert/Alert";
import Confirm from "components/atoms/alert/Confirm";
import Button from "components/atoms/button";
import useLoading from "components/atoms/loading/useLoading";
import Text from "components/atoms/text/Text";

import { useUser } from "hooks/queries/useUser";

import Constants from "utils/constants";
import { deleteReferencePdfFile, restartReferenceFileIndexing } from "utils/request/reference";
import { apiSpaceUrl } from "utils/urls";
import { isReadonlyUser } from "utils/user";

import axios from "axios";
import { saveAs } from "file-saver";

const Container = styled.div`
    width: 100%;
`;

const SubSection = styled.div`
    display: flex;
    flex-direction: column;
    gap: 8px;
    padding-bottom: 16px;

    & + & {
        padding-bottom: 16px;
    }
`;

const SubSectionHeader = styled.div`
    display: flex;
    align-items: center;
    gap: 4px;
`;

const SubSectionHeaderActions = styled.div`
    display: inline-flex;
    align-items: center;
    gap: 4px;
    margin-left: auto;
`;

const CardRow = styled.div`
    display: flex;
    align-items: center;
`;

const CardTitleText = styled(Text).attrs({
    variant: "body-md-400",
    color: "var(--color-Black)",
})``;

const CardText = styled(Text).attrs({
    variant: "body-sm-400",
    color: "var(--color-SubBlack)",
})``;

const FileNameText = styled(CardText)`
    min-width: 0;
`;

const IndexingInfoGaugeRow = styled.div`
    position: relative;
    width: 100%;
    height: 8px;
    border-radius: 4px;
    background-color: var(--color-Grey3);
    overflow: hidden;
`;

const IndexingInfoGauge = styled.div`
    position: absolute;
    top: 0;
    left: 0;
    height: 8px;
    background-color: ${(props) => (props.$warn ? "var(--color-Warn)" : "var(--color-Key)")};
    width: ${(props) => props.$gauge || 0}%;
    transition: width 0.3s ease;
`;

const FailureMessage = styled.div`
    width: 100%;
    padding: 8px 10px;
    border-radius: 6px;
    background-color: var(--color-Warn);
`;

const FailureTitle = styled(Text).attrs({
    variant: "body-sm-500",
    color: "var(--color-White)",
})``;

const FailureBody = styled(Text).attrs({
    variant: "body-sm-400",
    color: "var(--color-White)",
})`
    margin-top: 3px;
`;

const FAILURE_REASON_MESSAGES = {
    [Constants.FILE_ANALYSIS_FAILURE_OPEN_PARSE]: {
        title: "파일을 열 수 없습니다",
        body: "손상되었거나 암호가 걸린 파일일 수 있습니다. 확인 후 다시 업로드해 주세요.",
        retryable: false,
    },
    [Constants.FILE_ANALYSIS_FAILURE_OCR]: {
        title: "페이지 인식 실패",
        body: "이미지 페이지의 문자 인식에 실패했습니다. 문제가 계속되면 관리자에게 문의해 주세요.",
        retryable: true,
    },
    [Constants.FILE_ANALYSIS_FAILURE_EMBEDDING]: {
        title: "페이지 임베딩 실패",
        body: "페이지를 검색 가능한 형태로 변환 중 오류가 발생했습니다. 문제가 계속되면 관리자에게 문의해 주세요.",
        retryable: true,
    },
    [Constants.FILE_ANALYSIS_FAILURE_UNKNOWN]: {
        title: "분석 실패",
        body: "파일 분석 중 오류가 발생했습니다. 문제가 계속되면 관리자에게 문의해 주세요.",
        retryable: true,
    },
};

function ReferenceFileAnalysisView(props) {
    const match = useMatch("/:space_uuid/*");
    const space_uuid = match?.params?.space_uuid ?? "";
    const { setLoading } = useLoading();
    const volumeQuery = useUser();
    const [volumeInfo, setVolumeInfo] = useState({});
    const fileInputRef = useRef(null);
    const [fileUploadStatus, setFileUploadStatus] = useState(null);
    const hasFile = !!props.reference?.file;
    const fileName = hasFile
        ? decodeURI(props.reference.file.split("/").pop() || "document.pdf")
        : "업로드된 PDF 파일이 없습니다.";

    const indexed_pages = props.pdfIndexingInfo?.indexed_pages || 0;
    const total_pages = props.pdfIndexingInfo?.total_pages || 0;
    const analysis_status = props.pdfIndexingInfo?.analysis_status || Constants.FILE_ANALYSIS_TODO;
    const analysis_failure_reason = props.pdfIndexingInfo?.analysis_failure_reason;
    const isFailed = analysis_status === Constants.FILE_ANALYSIS_FAILED;
    const isIndexingStatusVisible = hasFile && analysis_status !== Constants.FILE_ANALYSIS_DONE;
    const failureMessage =
        FAILURE_REASON_MESSAGES[analysis_failure_reason] ||
        FAILURE_REASON_MESSAGES[Constants.FILE_ANALYSIS_FAILURE_UNKNOWN];

    useEffect(() => {
        if (volumeQuery.data) {
            setVolumeInfo({ ...volumeQuery.data.current_space?.volume_info });
        }
    }, [volumeQuery.data]);

    const uploadFile = (uploadData) => {
        setLoading(true, "PDF 파일을 업로드 중입니다.");
        setFileUploadStatus("loading");
        axios
            .post(apiSpaceUrl("reference", space_uuid) + "/file", uploadData, {
                headers: { "Content-Type": "multipart/form-data" },
                onUploadProgress: (progressEvent) => {
                    const percentCompleted = Math.round((progressEvent.loaded * 100) / progressEvent.total);
                    setLoading(true, `PDF 파일을 업로드 중입니다. (${percentCompleted}%)`);
                    if (percentCompleted === 100) {
                        setLoading(true, "PDF 파일을 저장 중입니다.");
                    }
                },
            })
            .then(() => {
                volumeQuery.refetch();
                setFileUploadStatus(null);
                setLoading(false);
            })
            .catch((error) => {
                setLoading(false);
                setFileUploadStatus(null);
                Alert("warn", "에러", error.response?.data?.file || "PDF 파일 업로드 중 오류가 발생하였습니다.");
            });
    };

    const fileChangeHandler = (event) => {
        const file = event.target.files?.[0];
        if (!file) return;

        if (volumeInfo?.used + file.size > volumeInfo?.max) {
            Alert("warn", "에러", "파일 용량이 초과되었습니다.");
            return;
        }

        if (file.size > 50 * Constants.MB) {
            Alert("warn", "에러", "50MB 이상의 파일은 업로드할 수 없습니다.");
            return;
        }

        uploadFile({ file, reference_id: props.reference.id });
    };

    const handleDeleteFile = () => {
        Confirm("warn", "PDF 삭제", "PDF 파일을 삭제하시겠습니까? 대화 기록도 함께 삭제됩니다.", "삭제", async () => {
            try {
                await deleteReferencePdfFile(space_uuid, { reference_id: props.reference.id });
            } catch (error) {
                console.log(error);
                Alert("warn", "에러", "PDF 파일 삭제 중 오류가 발생하였습니다.\n잠시후 다시 시도해주세요.");
            }
        });
    };

    const handleDownloadFile = () => {
        if (!props.reference?.file) return;
        saveAs(props.reference.file, fileName);
    };

    const handleIndexingRestart = async () => {
        try {
            await restartReferenceFileIndexing(space_uuid, props.reference.file_id);
            Alert("info", "알림", "인덱싱을 다시 시작합니다.");
        } catch (e) {
            console.log(e);
            Alert("warn", "에러", "요청이 실패했습니다.");
        }
    };

    // region: 마크업
    return (
        <Container>
            <SubSection>
                <SubSectionHeader>
                    <Icon name="ai" size={"14"} color={"var(--color-Grey1)"} />
                    <CardText>파일 관리</CardText>
                    {hasFile && (
                        <SubSectionHeaderActions>
                            <Tooltip message="PDF 다운로드">
                                <Button
                                    onlyIcon
                                    buttonSize={"20px"}
                                    bgColor={"var(--color-White)"}
                                    hoverBgColor={"var(--color-ButtonHover4)"}
                                    borderRadius={"2px"}
                                    onClick={handleDownloadFile}
                                >
                                    <Icon name={"download"} size={"12"} color={"var(--color-SubBlack)"} />
                                </Button>
                            </Tooltip>
                            <Tooltip message="PDF 삭제">
                                <Button
                                    onlyIcon
                                    buttonSize={"20px"}
                                    bgColor={"var(--color-White)"}
                                    hoverBgColor={"var(--color-ButtonHover4)"}
                                    borderRadius={"2px"}
                                    onClick={handleDeleteFile}
                                    disabled={
                                        analysis_status === Constants.FILE_ANALYSIS_DOING || isReadonlyUser(props.user)
                                    }
                                >
                                    <Icon name={"delete"} size={"12"} color={"var(--color-SubBlack)"} />
                                </Button>
                            </Tooltip>
                        </SubSectionHeaderActions>
                    )}
                </SubSectionHeader>
                <input
                    ref={fileInputRef}
                    type="file"
                    accept="application/pdf"
                    style={{ display: "none" }}
                    onChange={fileChangeHandler}
                />
                {hasFile ? (
                    <CardRow>
                        <FileNameText ellipsis>{fileName}</FileNameText>
                    </CardRow>
                ) : (
                    <Button
                        onlyText
                        width={"100%"}
                        height={"24px"}
                        bgColor={"var(--color-Button1)"}
                        hoverBgColor={"var(--color-ButtonHover1)"}
                        fontSize={"small"}
                        fontColor={"var(--color-White)"}
                        buttonText={"업로드"}
                        onClick={() => {
                            fileInputRef.current.value = null;
                            fileInputRef.current.click();
                        }}
                        disabled={fileUploadStatus === "loading" || isReadonlyUser(props.user)}
                    />
                )}
            </SubSection>

            {isIndexingStatusVisible && (
                <SubSection>
                    <SubSectionHeader>
                        <Icon name="ai" size={"14"} color={"var(--color-Grey1)"} />
                        <CardText>인덱싱 현황</CardText>
                        {(analysis_status === Constants.FILE_ANALYSIS_TODO ||
                            analysis_status === Constants.FILE_ANALYSIS_DOING) && (
                            <Tooltip message={"분석중입니다"}>
                                <AiCursor />
                            </Tooltip>
                        )}
                    </SubSectionHeader>
                    <CardRow>
                        <CardText>
                            AI 가 PDF 파일의 내용을 바탕으로 답변하기 위해, 파일의 구조를 분석하고 검색 가능한 형태로
                            변환합니다.
                        </CardText>
                    </CardRow>
                    <CardRow style={{ display: "flex", justifyContent: "space-between" }}>
                        <div style={{ display: "flex", alignItems: "center", gap: "8px" }}>
                            <CardTitleText
                                style={{
                                    margin: "8px 0px 8px 0px",
                                    color: isFailed ? "var(--color-Warn)" : "var(--color-DarkKey)",
                                }}
                            >
                                {"STATUS: " + analysis_status}
                            </CardTitleText>
                            {(analysis_status === Constants.FILE_ANALYSIS_TODO ||
                                analysis_status === Constants.FILE_ANALYSIS_DOING) && (
                                <Tooltip message={"분석중입니다"}>
                                    <Icon name={"load"} size={"12"}></Icon>
                                </Tooltip>
                            )}
                        </div>
                        <CardText>{indexed_pages + " / " + total_pages} 페이지</CardText>
                    </CardRow>
                    <CardRow>
                        <IndexingInfoGaugeRow>
                            <IndexingInfoGauge
                                $warn={isFailed}
                                $gauge={total_pages ? (indexed_pages / total_pages) * 100 : 0}
                            />
                        </IndexingInfoGaugeRow>
                    </CardRow>
                    {isFailed && (
                        <CardRow>
                            <FailureMessage>
                                <FailureTitle>{failureMessage.title}</FailureTitle>
                                <FailureBody>{failureMessage.body}</FailureBody>
                            </FailureMessage>
                        </CardRow>
                    )}
                    {isFailed && failureMessage.retryable && (
                        <CardRow>
                            <Button
                                onlyText
                                width={"100%"}
                                height={"24px"}
                                bgColor={"var(--color-HighlightRed)"}
                                hoverBgColor={"var(--color-WarnHover)"}
                                fontSize={"small"}
                                fontColor={"var(--color-White)"}
                                buttonText={"다시 시작"}
                                onClick={handleIndexingRestart}
                                disabled={!hasFile || isReadonlyUser(props.user)}
                            />
                        </CardRow>
                    )}
                </SubSection>
            )}
        </Container>
    );
}

export default ReferenceFileAnalysisView;
